// src/services/activitiesService.js
// Service for managing event activities

import { getSupabaseClient } from "./supabaseClient.js";

/**
 * List all activities for a specific event
 */
export async function listActivitiesByEvent(eventId, options = {}) {
    const { orderBy = "scheduled_date", ascending = true } = options;
    const supabase = getSupabaseClient();

    const { data, error } = await supabase
        .from("activities")
        .select(`
      *,
      assigned_profile:profiles!assigned_to(user_id, display_name)
    `)
        .eq("event_id", eventId)
        .order(orderBy, { ascending });

    if (error) throw error;
    return data || [];
}

/**
 * Get the next pending activity for an event
 */
export async function getNextActivity(eventId) {
    const supabase = getSupabaseClient();

    const { data, error } = await supabase
        .from("activities")
        .select(`
      *,
      assigned_profile:profiles!assigned_to(user_id, display_name)
    `)
        .eq("event_id", eventId)
        .eq("status", "pending")
        .gte("scheduled_date", new Date().toISOString())
        .order("scheduled_date", { ascending: true })
        .limit(1)
        .maybeSingle();

    if (error) throw error;
    return data;
}

/**
 * Get upcoming activities for a user (next 7 days)
 */
export async function getUpcomingActivitiesForUser(userId, options = {}) {
    const { days = 7, limit = 10 } = options;
    const supabase = getSupabaseClient();

    const now = new Date();
    const futureDate = new Date();
    futureDate.setDate(futureDate.getDate() + days);

    const { data, error } = await supabase
        .from("activities")
        .select(`
      *,
      event:events(id, client_name_legacy, date),
      assigned_profile:profiles!assigned_to(user_id, display_name)
    `)
        .eq("assigned_to", userId)
        .eq("status", "pending")
        .gte("scheduled_date", now.toISOString())
        .lte("scheduled_date", futureDate.toISOString())
        .order("scheduled_date", { ascending: true })
        .limit(limit);

    if (error) throw error;
    return data || [];
}

/**
 * Create a new activity
 */
export async function createActivity(activity) {
    const supabase = getSupabaseClient();

    const { data, error } = await supabase
        .from("activities")
        .insert(activity)
        .select(`
      *,
      assigned_profile:profiles!assigned_to(user_id, display_name)
    `)
        .single();

    if (error) throw error;
    return data;
}

/**
 * Update an existing activity
 */
export async function updateActivity(id, updates) {
    const supabase = getSupabaseClient();

    const { data, error } = await supabase
        .from("activities")
        .update(updates)
        .eq("id", id)
        .select(`
      *,
      assigned_profile:profiles!assigned_to(user_id, display_name)
    `)
        .single();

    if (error) throw error;
    return data;
}

/**
 * Delete an activity
 */
export async function deleteActivity(id) {
    const supabase = getSupabaseClient();

    const { error } = await supabase
        .from("activities")
        .delete()
        .eq("id", id);

    if (error) throw error;
}

/**
 * Mark an activity as completed
 */
export async function completeActivity(id, outcome, outcomeNotes) {
    return updateActivity(id, {
        status: "completed",
        completed_date: new Date().toISOString(),
        outcome,
        outcome_notes: outcomeNotes
    });
}

/**
 * Activity type labels
 */
export function getActivityTypeLabel(type) {
    const labels = {
        call: "Llamada",
        email: "Email",
        meeting: "Reunión",
        whatsapp: "WhatsApp",
        visit: "Visita",
        follow_up: "Seguimiento",
        proposal: "Propuesta",
        contract: "Contrato",
        payment: "Pago",
        task: "Tarea",
        other: "Otro"
    };
    return labels[type] || type;
}

/**
 * Activity status labels
 */
export function getActivityStatusLabel(status) {
    const labels = {
        pending: "Pendiente",
        completed: "Completada",
        cancelled: "Cancelada"
    };
    return labels[status] || status;
}

/**
 * Activity outcome labels
 */
export function getActivityOutcomeLabel(outcome) {
    const labels = {
        contacted: "Contactado",
        no_answer: "No responde",
        left_message: "Mensaje dejado",
        meeting_scheduled: "Cita programada",
        proposal_sent: "Presupuesto enviado",
        sold: "Vendido / Confirmado",
        lost: "Descartado / Perdido",
        other: "Otro"
    };
    return labels[outcome] || outcome;
}

/**
 * Get list of valid outcomes
 */
export function getActivityOutcomes() {
    return [
        { id: "contacted", label: "Contactado" },
        { id: "no_answer", label: "No responde" },
        { id: "left_message", label: "Mensaje dejado" },
        { id: "meeting_scheduled", label: "Cita programada" },
        { id: "proposal_sent", label: "Presupuesto enviado" },
        { id: "sold", label: "Vendido / Confirmado" },
        { id: "lost", label: "Descartado / Perdido" },
        { id: "other", label: "Otro" }
    ];
}
/**
 * List all pending activities for a team
 */
export async function listPendingActivitiesByTeam(teamId, options = {}) {
    const { limit = 20 } = options;
    const supabase = getSupabaseClient();

    const { data, error } = await supabase
        .from("activities")
        .select(`
            *,
            event:events(client_name_legacy),
            assigned_profile:profiles!assigned_to(display_name)
        `)
        .eq("team_id", teamId)
        .eq("status", "pending")
        .order("scheduled_date", { ascending: true })
        .limit(limit);

    if (error) throw error;
    return data || [];
}
